import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import { QueryClientProvider } from "react-query";
import { SnackbarProvider } from "notistack";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { Box, CircularProgress, CssBaseline } from "@mui/material";

import queryClient from "./base/QueryClient";
import routes from "./route";
import Header from "./components/Header/Header";
import Sidebar from "./components/SideBar/SideBar";
import LoginScreen from "./screens/LoginScreen";
import LocationProvider from "./context/LocationProvider";
import { AuthProvider, useAuth } from "./context/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import SetPassword from "./screens/SetPassword/SetPassword";
import ResendLink from "./screens/SetPassword/ResendLink";

const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#1976d2",
    },
    background: {
      default: "#F4F6F8",
      paper: "#ffffff",
    },
  },
});

function Layout() {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  const isLoginPage = location.pathname === "/login";
  const isSetPasswordPage = location.pathname === "/set-password";
  const isResendLinkPage = location.pathname === "/resend-link";

  const isAuthPage = isLoginPage || isSetPasswordPage || isResendLinkPage;

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100vh",
          bgcolor: "background.default",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isAuthPage) {
    return (
      <Box
        sx={{
          display: "flex",
          minHeight: "100vh",
          bgcolor: "background.default",
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box
            component="main"
            sx={{
              flexGrow: 1,
              overflow: "auto",
            }}
          >
            <Routes>
              <Route path="/login" element={<LoginScreen />} />
              <Route path="/set-password" element={<SetPassword />} />
              <Route path="/resend-link" element={<ResendLink />} />
            </Routes>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
      {isAuthenticated && <Sidebar />}
      <Box
        sx={{
          flexGrow: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {isAuthenticated && <Header />}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 3,
            overflow: "auto",
            bgcolor: "background.default",
          }}
        >
          <Routes>
            {routes.map((route, index) => (
              <Route
                key={index}
                path={route.path}
                element={<ProtectedRoute>{route.element}</ProtectedRoute>}
              >
                {route.children &&
                  route.children.map((child, childIndex) => (
                    <Route
                      key={childIndex}
                      path={child.path}
                      element={
                        <ProtectedRoute>{child.element}</ProtectedRoute>
                      }
                    />
                  ))}
              </Route>
            ))}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Box>
      </Box>
    </Box>
  );
}

function App() {
  return (
    <SnackbarProvider maxSnack={3}>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <LocationProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Router>
                <AuthProvider>
                  <Layout />
                </AuthProvider>
              </Router>
            </LocalizationProvider>
          </LocationProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </SnackbarProvider>
  );
}

export default App;